import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../shared/lib/supabase'
import { logAuditEvent } from '../../../shared/lib/auditLog'

function AuthCallback() {
  const navigate = useNavigate()
  const logged = useRef(false)
  const [errorMsg, setErrorMsg] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const type = params.get('type') || 'unknown'
    const errorParam = params.get('error_description') || params.get('error')

    // If Supabase returned an error (e.g., expired link)
    if (errorParam) {
      setErrorMsg(errorParam)
      logAuditEvent({
        category: 'system',
        action: 'auth.callback_failed',
        description: `Auth callback error: ${errorParam}`,
        metadata: { type, error: errorParam }
      })
      return
    }

    const handleSuccess = (event) => {
      if (logged.current) return
      logged.current = true
      logAuditEvent({
        category: 'system',
        action: 'auth.callback_success',
        description: `Auth callback completed (event: ${event})`,
        metadata: { event, type }
      })

      // Password recovery events should go to reset-password page
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    }

    // Listen for auth state changes (works for both PKCE code exchange and legacy token_hash)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) handleSuccess(event)
    })

    // PKCE flow: exchange the authorization code for a session
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
        if (error) {
          console.error('[AuthCallback] Code exchange failed:', error.message)
          setErrorMsg('Your link has expired or was already used. Please request a new one.')
          logAuditEvent({
            category: 'system',
            action: 'auth.callback_failed',
            description: `PKCE code exchange failed: ${error.message}`,
            metadata: { type, error: error.message }
          })
        }
        // On success, onAuthStateChange will fire and handle navigation
      })
    } else {
      // Legacy flow fallback: check if session was auto-detected from URL hash/params
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) handleSuccess('session_detected')
      })
    }

    // Safety fallback: if nothing happens after 15s, show error instead of silently redirecting
    const timeout = setTimeout(() => {
      if (!logged.current) {
        setErrorMsg('Authentication timed out. The link may have expired — please request a new one.')
        logAuditEvent({
          category: 'system',
          action: 'auth.callback_failed',
          description: 'Auth callback timed out after 15 seconds',
          metadata: { reason: 'timeout', type, hadCode: !!code, url: window.location.href }
        })
      }
    }, 15000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeout)
    }
  }, [navigate])

  if (errorMsg) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ backgroundColor: '#012438' }}>
        <img src="/logo.png" alt="SSA Logo" className="w-16 h-16 rounded-2xl object-contain mb-6" />
        <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }}>
          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <p className="text-sm text-center mb-1" style={{ color: '#E5E7EB' }}>
          Unable to complete authentication
        </p>
        <p className="text-xs text-center max-w-sm mb-6" style={{ color: '#7E8F9C' }}>
          {errorMsg}
        </p>
        <a
          href="/login"
          className="px-4 py-2 text-sm font-medium rounded-lg"
          style={{ backgroundColor: '#8AD0D6', color: '#012438' }}
        >
          Go to Login
        </a>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#012438' }}>
      <img src="/logo.png" alt="SSA Logo" className="w-16 h-16 rounded-2xl object-contain mb-6" />
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 mb-4" style={{ borderColor: '#8AD0D6' }} />
      <p className="text-sm" style={{ color: '#7E8F9C' }}>
        Setting up your account...
      </p>
    </div>
  )
}

export default AuthCallback
